<template>
  <PopupDropdown
    v-if="editable"
    v-model="internalHealth"
    :options="healthOptions"
    option-label="label"
    option-value="value"
    class="shipment-health-chip"
  >
    <template #placeholder>
      <component :is="chip" />
    </template>
  </PopupDropdown>
  <component :is="chip" v-else class="shipment-health-chip" />
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, h, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  mapEnumToTranslatedDropdownValues,
  translateEnum,
} from '@rygen/rygen-ui/i18n';
import { Severity } from '@rygen/rygen-ui/model';

import { ShipmentHealth } from '@/model';

import PopupDropdown from '@rygen/rygen-ui/components/popup/PopupDropdown.vue';
import RygenChip from '@rygen/rygen-ui/components/ui/RygenChip.vue';

const props = defineProps({
  health: {
    type: String as PropType<ShipmentHealth>,
    default: () => '',
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { health, editable } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:health', newVal: ShipmentHealth): void;
}>();

const { t } = useI18n();

const internalHealth = ref(health.value);
const healthOptions = mapEnumToTranslatedDropdownValues(
  ShipmentHealth,
  'ShipmentHealth',
);

const severity = computed(() => {
  switch (internalHealth.value) {
    case ShipmentHealth.HEALTHY:
      return Severity.SUCCESS;
    case ShipmentHealth.AT_RISK:
      return Severity.WARNING;
    case ShipmentHealth.CRITICAL:
      return Severity.DANGER;
    default:
      return Severity.INFO;
  }
});

const chip = computed(() =>
  h(RygenChip, {
    label: internalHealth.value
      ? translateEnum(internalHealth.value, 'ShipmentHealth')
      : t('common.loading'),
    severity: severity.value,
  }),
);

watch(
  health,
  (newVal: ShipmentHealth) => {
    internalHealth.value = ShipmentHealth[newVal];
  },
  { immediate: true },
);

watch(internalHealth, (newVal: ShipmentHealth) => {
  emit('update:health', newVal);
});
</script>

<style>
.shipment-health-chip {
  &.popup-dropdown {
    display: inline-flex;
    width: min-content;
  }

  .p-chip-text {
    line-height: 1;
    margin-block: 0;
    font-size: 10px;
    font-weight: 700;
  }
}
</style>
